'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useReactiveRef } from './useReactiveRef';
import { useDispatch } from 'react-redux';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/lib/logger';
import { hydrateCallOwnership } from '@/store/slices/callOwnershipSlice';

/**
 * Call ownership, READ-ONLY in the browser (Q-2492 slice 2).
 *
 * calls.owner_id is the single owner of a call, written only by the server (the atomic claim_or_repair_call_scope).
 * This hook shows it: it loads the row, follows the row's realtime updates, and derives
 * `isOwner = (calls.owner_id === the signed-in user's id)`. The browser never writes ownership (no owner_id, owner_name
 * or claimed_at update), and a re-sync always shows what the row says: there is no "un-flushed local ownership wins"
 * rule any more (#725's dirty guard), because there is no local ownership to flush. A call-state claim response cannot
 * raise isOwner either: the writer lease it grants is a separate "this tab may write" flag (useCallStateSync).
 */

interface UseCallOwnershipSyncOptions {
  /** Call ID (room name) for scoping */
  callId: string;
  /** Whether sync is enabled (default: true) */
  enabled?: boolean;
  /** Unused since Q-2492 (the browser no longer writes ownership); kept so existing callers compile. */
  debounceMs?: number;
}

interface CallOwnershipSyncState {
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncTime: number | null;
  error: Error | null;
  dbCallId: string | null;
}

type OwnershipRow = {
  id?: string | null;
  owner_id?: string | null;
  owner_name?: string | null;
  claimed_at?: string | null;
};

const OWNERSHIP_COLUMNS = 'id, owner_id, owner_name, claimed_at';

/** The Redux ownership a calls row means for this user: owner_id decides, and only for the user it names. */
export function ownershipFromRow(row: OwnershipRow, userId: string | null | undefined) {
  return {
    ownerId: row.owner_id ?? null,
    ownerName: row.owner_name ?? null,
    isOwner: Boolean(row.owner_id) && row.owner_id === userId,
    claimedAt: row.claimed_at ? new Date(row.claimed_at).getTime() : null,
    updatedAt: Date.now(),
  };
}

/**
 * Hook that shows the call's ownership from the `calls` row (load + realtime).
 */
export function useCallOwnershipSync(
  options: UseCallOwnershipSyncOptions,
): CallOwnershipSyncState & { reloadFromSupabase: () => Promise<void> } {
  const { callId, enabled = true } = options;

  const dispatch = useDispatch();
  const { user, isLoading: authLoading } = useAuth();
  const supabase = createClient();
  const userIdRef = useRef<string | null>(user?.id ?? null);
  userIdRef.current = user?.id ?? null;

  const ownershipRowRef = useRef<OwnershipRow | null>(null);
  const mountedRef = useRef(true);
  const hasHydratedRef = useRef(false);
  const hydrationPendingRef = useRef(false);
  const readGenerationRef = useRef(0);
  // A client-side move to another call shows THAT call's row (useCallStateSync clears the old ownership on the move).
  const hydratedCallIdRef = useRef(callId);
  if (hydratedCallIdRef.current !== callId) {
    hydratedCallIdRef.current = callId;
    hasHydratedRef.current = false;
    ownershipRowRef.current = null;
    hydrationPendingRef.current = false;
    readGenerationRef.current += 1;
  }
  const stateRef = useReactiveRef<CallOwnershipSyncState>({
    isLoading: true,
    isSyncing: false,
    lastSyncTime: null,
    error: null,
    dbCallId: null,
  });

  const showRow = useCallback((row: OwnershipRow) => {
    if (!mountedRef.current) return;
    ownershipRowRef.current = row;
    dispatch(hydrateCallOwnership(ownershipFromRow(row, userIdRef.current)));
  }, [dispatch]);

  /**
   * Load ownership from the row (on mount, and forced on reconnect).
   */
  const loadFromSupabase = useCallback(async (opts?: { force?: boolean }) => {
    if (!enabled || !callId || (!opts?.force && hasHydratedRef.current)) {
      stateRef.current = { ...stateRef.current, isLoading: false };
      return;
    }
    if (hydrationPendingRef.current) return;
    hydrationPendingRef.current = true;
    const readGeneration = ++readGenerationRef.current;
    const isCurrentRead = () => mountedRef.current
      && hydratedCallIdRef.current === callId
      && readGenerationRef.current === readGeneration;

    try {
      const { data: callData, error: callError } = await supabase
        .from('calls')
        .select(OWNERSHIP_COLUMNS)
        .eq('call_id', callId)
        .maybeSingle();

      // A previous call's read, or a read superseded by realtime, cannot restore stale owner controls.
      if (!isCurrentRead()) return;
      if (callError && callError.code !== 'PGRST116') {
        throw callError;
      }

      hasHydratedRef.current = true;
      if (!callData?.id) {
        showRow({});
        stateRef.current = { ...stateRef.current, isLoading: false, dbCallId: null };
        return;
      }

      showRow(callData as OwnershipRow);
      logger.info('useCallOwnershipSync', 'Loaded from Supabase', {
        ownerId: callData.owner_id,
        isOwner: Boolean(callData.owner_id) && callData.owner_id === userIdRef.current,
      });

      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        lastSyncTime: Date.now(),
        error: null,
        dbCallId: callData.id,
      };
    } catch (err: unknown) {
      if (!isCurrentRead()) return;
      logger.error('useCallOwnershipSync', 'Failed to load from Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    } finally {
      if (isCurrentRead()) hydrationPendingRef.current = false;
    }
  }, [enabled, callId, supabase, showRow]);

  // #725: force a re-sync from the row on LiveKit reconnect. isReloadingRef collapses back-to-back reconnects into a
  // single in-flight fetch.
  const isReloadingRef = useRef(false);
  const reloadFromSupabase = useCallback(async () => {
    if (isReloadingRef.current) return;
    isReloadingRef.current = true;
    try {
      await loadFromSupabase({ force: true });
    } finally {
      isReloadingRef.current = false;
    }
  }, [loadFromSupabase]);

  // Remove the previous call's controls immediately while this call's row is loading.
  useEffect(() => { showRow({}); }, [callId, showRow]);

  // Load from Supabase on mount
  useEffect(() => {
    if (!enabled || authLoading) return;

    loadFromSupabase();
  }, [enabled, authLoading, loadFromSupabase]);

  // Authentication can change while this component remains mounted. Re-derive the flag from the same row.
  useEffect(() => {
    if (enabled && !authLoading && ownershipRowRef.current) showRow(ownershipRowRef.current);
  }, [enabled, authLoading, user?.id, showRow]);

  // Follow the row: a handover or the first claim moves owner_id on the server, and every tab shows it.
  useEffect(() => {
    if (!enabled || !callId || authLoading) return;
    let active = true;
    let teardown: (() => void) | null = null;
    try {
      const changes = { schema: 'public', table: 'calls', filter: `call_id=eq.${callId}` };
      const followRow = (payload: { new?: unknown }) => {
        if (!active || hydratedCallIdRef.current !== callId) return;
        const row = payload.new as OwnershipRow | null | undefined;
        if (!row) return;
        // Realtime is newer than any outstanding mount/reconnect read.
        readGenerationRef.current += 1;
        hydrationPendingRef.current = false;
        hasHydratedRef.current = true;
        showRow(row);
        stateRef.current = {
          ...stateRef.current, isLoading: false, lastSyncTime: Date.now(), error: null, dbCallId: row.id ?? null,
        };
      };
      const channel = supabase
        .channel(`call-ownership:${callId}`)
        .on('postgres_changes', { ...changes, event: 'INSERT' }, followRow)
        .on('postgres_changes', { ...changes, event: 'UPDATE' }, followRow)
        .subscribe();
      teardown = () => { void supabase.removeChannel(channel); };
    } catch (err: unknown) {
      // Realtime unavailable: the mount load and the reconnect re-sync still show the row.
      logger.warn('useCallOwnershipSync', 'Realtime ownership subscription failed', { error: err });
    }
    return () => { active = false; teardown?.(); };
  }, [enabled, callId, authLoading, supabase, showRow]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { ...stateRef.current, reloadFromSupabase };
}

/**
 * Hook to hydrate call ownership from Supabase (for use outside the main sync hook)
 */
export function useCallOwnershipHydration(callId: string): {
  hydrate: () => Promise<void>;
  isHydrated: boolean;
} {
  const dispatch = useDispatch();
  const supabase = createClient();
  const { user } = useAuth();
  const hydratedRef = useRef(false);

  const hydrate = useCallback(async () => {
    if (hydratedRef.current || !callId) return;

    try {
      const { data: callData } = await supabase
        .from('calls')
        .select(OWNERSHIP_COLUMNS)
        .eq('call_id', callId)
        .maybeSingle();

      if (callData && (callData.owner_id || callData.owner_name || callData.claimed_at)) {
        dispatch(hydrateCallOwnership(ownershipFromRow(callData as OwnershipRow, user?.id)));

        logger.info('useCallOwnershipHydration', 'Hydrated from Supabase', {
          ownerId: callData.owner_id,
        });
      }

      hydratedRef.current = true;
    } catch (err: unknown) {
      logger.error('useCallOwnershipHydration', 'Hydration failed', { error: err });
    }
  }, [callId, dispatch, supabase, user?.id]);

  return {
    hydrate,
    isHydrated: hydratedRef.current,
  };
}

export default useCallOwnershipSync;
